import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

import queryString from "query-string";
import { useDealActions, usePortfolioConstructionActions, useReportsActions, useUserActions } from "store";
import { CorrelationMatrixSettings, IBookmark, QuickViewQueries, UrlQueryParams } from "store/types";
import { reportStateFromQueries } from "utils/reports-state-from-queries";

const normalizeEmail = (params: string) => params.replace(/(email.+)\+/g, "%2B");

export const useApplyBookmark = (): ((bookmark: IBookmark) => void) => {
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { setSelectedBookmark } = useUserActions();
  const { setQuickViewQueriesAction, replaceQueriesAction, setIgnoreDefaultView } = useReportsActions();
  const { updateQueries } = useDealActions();
  const { setCorrelationMatrixSettingsAction } = usePortfolioConstructionActions();

  return useCallback(
    (bookmark: IBookmark) => {
      const { params, type, id } = bookmark;
      const bookmarkPath = params.split("?")[0];

      if (type === "quick_view") {
        const normalized = normalizeEmail(params);
        const { quickViewQueries } = queryString.parseUrl(normalized).query as UrlQueryParams;
        if (!quickViewQueries) return;
        setSelectedBookmark(id);
        // Keep the selection through navigation: the DefaultViewGuard unmount cleanup
        // clears selectedBookmark unless ignoreDefaultView is set (deal/report do this
        // implicitly via their reducers; quick_view must set it explicitly).
        setIgnoreDefaultView(true);
        setQuickViewQueriesAction({
          queries: JSON.parse(quickViewQueries as string) as QuickViewQueries,
          replace: true
        });
        if (pathname !== bookmarkPath) {
          setTimeout(() => push(bookmarkPath), 0);
        }
        return;
      }

      if (type === "deal" || type === "investor_view") {
        const { deals: dealsFromAPI } = queryString.parseUrl(params).query;
        if (!dealsFromAPI || Array.isArray(dealsFromAPI)) return;
        setSelectedBookmark(id);
        updateQueries(JSON.parse(dealsFromAPI), true);
        setTimeout(
          () => push({ pathname: bookmarkPath, search: `?deals=${encodeURIComponent(dealsFromAPI)}` }),
          0
        );
        return;
      }

      if (type === "report") {
        if (!params || Array.isArray(params)) return;
        setSelectedBookmark(id);
        const queries = reportStateFromQueries(params);
        if (queries) replaceQueriesAction({ queries });
        setTimeout(() => push(bookmarkPath), 0);
        return;
      }

      if (type === "correlation_matrix") {
        const normalized = normalizeEmail(params);
        const { correlationMatrixSettings } = queryString.parseUrl(normalized).query as UrlQueryParams;
        if (!correlationMatrixSettings) return;
        setSelectedBookmark(id);
        setIgnoreDefaultView(true);
        setCorrelationMatrixSettingsAction(
          JSON.parse(correlationMatrixSettings as string) as CorrelationMatrixSettings
        );
        setTimeout(() => push(bookmarkPath), 0);
        return;
      }

      if (type === "data_operations") {
        const normalized = normalizeEmail(params);
        const { dataOperations } = queryString.parseUrl(normalized).query as UrlQueryParams;
        if (!dataOperations) return;
        setSelectedBookmark(id);
        setIgnoreDefaultView(true);
        setTimeout(
          () =>
            push({
              pathname: bookmarkPath,
              search: `?dataOperations=${encodeURIComponent(dataOperations as string)}`
            }),
          0
        );
        return;
      }
    },
    [
      push,
      pathname,
      setSelectedBookmark,
      setQuickViewQueriesAction,
      replaceQueriesAction,
      updateQueries,
      setCorrelationMatrixSettingsAction,
      setIgnoreDefaultView
    ]
  );
};
